import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IEvent, ILocation } from "@dc-ireland/shared"
import ImagePicker from "../../components/admin/ImagePicker"
import AdminAlerts from "../../components/admin/AdminAlerts"

const emptyForm = {
  title: "",
  subtitle: "",
  description: "",
  location: "",
  chapterSlug: "",
  startDate: "",
  startTime: "",
  endDate: "",
  endTime: "",
  imageUrl: "",
  registerLabel: "Register",
  registerUrl: "",
  showCardText: true,
}

const pad = (n: number) => String(n).padStart(2, "0")

const toDatePart = (value: Date | string | undefined | null): string => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const toTimePart = (value: Date | string | undefined | null): string => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const toIsoDateTime = (date: string, time: string): string | undefined => {
  if (!date) return undefined
  const combined = new Date(`${date}T${time || "00:00"}`)
  if (Number.isNaN(combined.getTime())) return undefined
  return combined.toISOString()
}

const AdminEvents = () => {
  const [events, setEvents] = useState<IEvent[]>([])
  const [locations, setLocations] = useState<ILocation[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiClient.getEvents()
      setEvents(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load events")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadEvents()
    apiClient.getLocations().then(setLocations).catch(() => setLocations([]))
  }, [])

  const resetForm = () => {
    setForm(emptyForm)
    setEditingId(null)
  }

  const startEdit = (event: IEvent) => {
    if (!event.id) return
    setEditingId(event.id)
    setForm({
      title: event.title,
      subtitle: event.subtitle ?? "",
      description: event.description ?? "",
      location: event.location ?? "",
      chapterSlug: event.chapterSlug ?? "",
      startDate: toDatePart(event.startDate),
      startTime: toTimePart(event.startDate),
      endDate: toDatePart(event.endDate),
      endTime: toTimePart(event.endDate),
      imageUrl: event.imageUrl ?? "",
      registerLabel: event.registerLabel || "Register",
      registerUrl: event.registerUrl ?? "",
      showCardText: event.showCardText !== false,
    })
    setError(null)
    setSuccess(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    const payload = {
      title: form.title,
      subtitle: form.subtitle || undefined,
      description: form.description || undefined,
      location: form.location || undefined,
      chapterSlug: form.chapterSlug || undefined,
      startDate: toIsoDateTime(form.startDate, form.startTime)!,
      endDate: toIsoDateTime(
        form.endDate || (form.endTime ? form.startDate : ""),
        form.endTime || (form.endDate ? form.startTime : "")
      ),
      imageUrl: form.imageUrl || undefined,
      registerLabel: form.registerLabel || undefined,
      registerUrl: form.registerUrl || undefined,
      showCardText: form.showCardText,
    }

    try {
      if (editingId) {
        await apiClient.updateEvent(editingId, payload)
        setSuccess("Event updated.")
      } else {
        await apiClient.createEvent(payload)
        setSuccess("Event created successfully.")
      }
      resetForm()
      await loadEvents()
    } catch (err) {
      setError(err instanceof Error ? err.message : editingId ? "Failed to update event" : "Failed to create event")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this event?")) return
    try {
      setError(null)
      await apiClient.deleteEvent(id)
      setSuccess("Event deleted.")
      if (editingId === id) resetForm()
      await loadEvents()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete event")
    }
  }

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold">Events</h2>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          <h3 className="card-title">{editingId ? "Edit Event" : "Create Event"}</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="form-control">
              <span className="label-text">Title *</span>
              <input
                className="input input-bordered"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Subtitle</span>
              <input
                className="input input-bordered"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Description</span>
              <textarea
                className="textarea textarea-bordered"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Venue / address</span>
              <input
                className="input input-bordered"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Chapter</span>
              <select
                className="select select-bordered"
                value={form.chapterSlug}
                onChange={(e) => setForm({ ...form, chapterSlug: e.target.value })}
              >
                <option value="">All chapters (national)</option>
                {locations.map((loc) => (
                  <option key={loc.slug} value={loc.slug}>
                    {loc.name}
                  </option>
                ))}
                <option value="external">External</option>
              </select>
            </label>
            <label className="form-control">
              <span className="label-text">Register button label</span>
              <input
                className="input input-bordered"
                value={form.registerLabel}
                onChange={(e) => setForm({ ...form, registerLabel: e.target.value })}
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Registration link</span>
              <input
                type="url"
                className="input input-bordered"
                value={form.registerUrl}
                onChange={(e) => setForm({ ...form, registerUrl: e.target.value })}
                placeholder="https://..."
              />
            </label>
            <label className="form-control">
              <span className="label-text">Start date *</span>
              <input
                type="date"
                className="input input-bordered"
                required
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Start time *</span>
              <input
                type="time"
                className="input input-bordered"
                required
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">End date</span>
              <input
                type="date"
                className="input input-bordered"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
            <label className="form-control">
              <span className="label-text">End time</span>
              <input
                type="time"
                className="input input-bordered"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </label>
            <div className="md:col-span-2">
              <ImagePicker
                label="Event image"
                value={form.imageUrl}
                onChange={(imageUrl) => setForm({ ...form, imageUrl })}
                hint="We show the full image, no crop. Best fit: landscape 16:9 at 1920×1080. Other shapes keep the whole flyer with empty space around it."
              />
            </div>
            <label className="label cursor-pointer justify-start gap-3 md:col-span-2">
              <input
                type="checkbox"
                className="checkbox checkbox-primary"
                checked={form.showCardText}
                onChange={(e) => setForm({ ...form, showCardText: e.target.checked })}
              />
              <span>
                <span className="label-text">Show title and details under the image</span>
                <span className="block text-xs text-secondary">
                  Turn off when the flyer already has the date, venue, and title.
                </span>
              </span>
            </label>
          </div>
          <div className="card-actions justify-end">
            {editingId && (
              <button type="button" onClick={resetForm} className="btn btn-ghost">
                Cancel edit
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update Event" : "Create Event"}
            </button>
          </div>
        </div>
      </form>

      <div className="overflow-x-auto">
        <h3 className="mb-4 text-lg font-semibold">Existing Events</h3>
        {loading ? (
          <span className="loading loading-spinner loading-md"></span>
        ) : events.length === 0 ? (
          <p className="text-secondary">No events yet.</p>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Title</th>
                <th>Start</th>
                <th>Chapter</th>
                <th>Venue</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{event.title}</td>
                  <td>{event.startDate ? new Date(event.startDate).toLocaleString("en-IE") : "—"}</td>
                  <td>{event.chapterSlug ?? "All"}</td>
                  <td>{event.location ?? "—"}</td>
                  <td className="space-x-2">
                    {event.id && (
                      <>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => startEdit(event)}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-error btn-sm" onClick={() => handleDelete(event.id!)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default AdminEvents
